import {
  type KeyboardEvent,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import { useAppDispatch } from "app/hooks";
import {
  setContent as setConfirmDialogContent,
  setOnChange as setConfirmDialogOnChange,
  setOpen as setConfirmDialogOpen,
  setTitle as setConfirmDialogTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import type { TemplateSourceType } from "services/suno-comms/types/text-templates.type";
import { TextTemplate } from "services/types/text-templates.type";
import { convertSunoPlaceholdersToMergeTags } from "utils/suno-merge-tags.util";

import { exportBuilderHtmlForStorage } from "./email-builder-export.util";
import type { EmailBuilderMergeTags } from "./email-builder-merge-tags";
import EmailBuilderComponent from "./email-builder.component";

type FillHeightEditorPaneProps = {
  visible: boolean;
  children: React.ReactNode;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
};

function FillHeightEditorPane({
  visible,
  children,
  onKeyDown
}: FillHeightEditorPaneProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState<number>();

  useEffect(() => {
    if (!visible) return;

    const element = containerRef.current;
    if (!element) return;

    const updateHeight = () => {
      const nextHeight = element.clientHeight;
      if (nextHeight > 0) {
        setEditorHeight(nextHeight);
      }
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [visible]);

  const resolvedContent =
    isValidElement(children) && editorHeight
      ? cloneElement(children, {
          height: editorHeight,
          ...(onKeyDown ? { onKeyDown } : {})
        })
      : children;

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        display: visible ? "flex" : "none",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      {resolvedContent}
    </Box>
  );
}

export enum EmailEditorTab {
  Editor = 0,
  EmailBuilder = 1
}

export type EmailEditorTabsProps = {
  emailBuilderRef: React.MutableRefObject<UnlayerEditor | null>;
  initialMessage?: string;
  initialDesign?: UnlayerDesign | null;
  showTabs?: boolean;
  onExportFromBuilder?: (html: string) => void;
  editorContent: React.ReactNode;
  tabLabels?: {
    editor?: string;
    emailBuilder?: string;
  };
  tabsSx?: Record<string, any>;
  activeTab?: EmailEditorTab;
  onTabChange?: (tab: EmailEditorTab) => void;
  setActiveDesign: (design: UnlayerDesign | null) => void;
  editorTabIsDisabled: boolean;
  setEditorTabIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * When true, the regular editor tab stays clickable even after a drag & drop
   * design has been loaded or edited. Switching back from the builder prompts a
   * confirmation and converts the design to HTML via onExportFromBuilder.
   * When false (default), switching back is blocked once a design exists.
   */
  confirmSwitchToEditor?: boolean;
  /**
   * Callback when design is updated in the builder.
   * Used to detect changes for enabling save buttons.
   */
  onDesignChange?: () => void;
  /**
   * Template types to filter in the templates dialog.
   * When not defined, uses EMAIL_TEMPLATE_DESIGN_ONLY_TYPES.
   */
  templateTypes?: number[];
  /**
   * Callback to handle key down events in the editor content.
   * This will be passed to the editorContent if it's a valid React element.
   */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Whether this instance is active and should respond to global events.
   * When multiple instances are mounted, only the active one responds to
   * events like opening the templates modal.
   */
  isActive?: boolean;
  mergeTags?: EmailBuilderMergeTags;
  specialLinks?: UnlayerSpecialLink[];
  /** Backend to read templates from. Omitted ⇒ main backend. */
  templateSource?: TemplateSourceType;
  /** Raise the templates dialog above nested panels (e.g. leads inbox). */
  dialogZIndex?: number;
  /** Called when a template is inserted from the builder templates dialog. */
  onTemplateInserted?: (template: TextTemplate) => void;
  /** Called when the Unlayer editor finishes initializing or re-initializing. */
  onEditorReady?: () => void;
  /** Optional transform applied to designer template JSON before load (e.g. resolve merge tags). */
  transformTemplateDesign?: (design: UnlayerDesign) => UnlayerDesign;
  /**
   * Pixels to subtract from 95vh for the builder height (default 500).
   * Pass a larger value in contexts with less surrounding chrome.
   */
  builderHeightOffset?: number;
  /**
   * When true, the builder fills its container (width and height 100%).
   * Used inside large modals instead of viewport-based height.
   */
  fillContainer?: boolean;
  /**
   * Wraps the editor and builder panes (not the tabs), e.g. to draw the email branding the backend
   * adds around them. Omitted ⇒ the panes render as before.
   */
  contentWrapper?: (panes: React.ReactNode) => React.ReactNode;
};

export default function EmailEditorTabs({
  emailBuilderRef,
  initialMessage,
  initialDesign,
  showTabs = true,
  onExportFromBuilder,
  editorContent,
  tabLabels = {
    editor: "Editor",
    emailBuilder: "Drag & Drop Email Builder"
  },
  tabsSx,
  activeTab: controlledTab,
  onTabChange,
  setActiveDesign,
  editorTabIsDisabled,
  setEditorTabIsDisabled,
  confirmSwitchToEditor = false,
  onDesignChange,
  templateTypes,
  onKeyDown,
  isActive = true,
  mergeTags,
  specialLinks,
  templateSource,
  dialogZIndex,
  onTemplateInserted,
  onEditorReady,
  transformTemplateDesign,
  builderHeightOffset,
  fillContainer,
  contentWrapper
}: EmailEditorTabsProps) {
  const [internalTab, setInternalTab] = useState<EmailEditorTab>(
    EmailEditorTab.Editor
  );
  const [shouldMountBuilder, setShouldMountBuilder] = useState(
    Boolean(initialDesign)
  );

  // Support both controlled and uncontrolled modes
  const isControlled = controlledTab !== undefined;
  const currentTab = isControlled ? controlledTab : internalTab;

  const dispatch = useAppDispatch();

  // Convert the builder design to HTML, hand it to the regular editor, re-enable
  // the editor tab, and activate it.
  const switchToEditor = useCallback(() => {
    emailBuilderRef.current?.exportHtml((data) => {
      const htmlWithMergeTags = convertSunoPlaceholdersToMergeTags(data.html);
      onExportFromBuilder?.(htmlWithMergeTags);
    });

    setEditorTabIsDisabled(false);

    if (!isControlled) {
      setInternalTab(EmailEditorTab.Editor);
    }

    onTabChange?.(EmailEditorTab.Editor);
  }, [
    emailBuilderRef,
    onExportFromBuilder,
    setEditorTabIsDisabled,
    isControlled,
    onTabChange
  ]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: EmailEditorTab) => {
      const switchingBackToEditor =
        currentTab === EmailEditorTab.EmailBuilder &&
        newValue === EmailEditorTab.Editor &&
        editorTabIsDisabled;

      // Leaving the drag & drop builder converts the design to HTML, so confirm
      // before discarding the builder layout when this behavior is opted into.
      if (switchingBackToEditor && confirmSwitchToEditor) {
        dispatch(setConfirmDialogTitle("Switch to the regular editor?"));
        dispatch(
          setConfirmDialogContent(
            "Your drag & drop design will be converted to HTML and opened in the regular editor. The drag & drop layout may be lost. Do you want to continue?"
          )
        );
        dispatch(
          setConfirmDialogOnChange(() => {
            switchToEditor();
            dispatch(setConfirmDialogOpen(false));
          })
        );
        dispatch(setConfirmDialogOpen(true));
        return;
      }

      // When switching FROM emailBuilder TO editor, export HTML only if changes were made
      if (switchingBackToEditor) {
        emailBuilderRef.current?.exportHtml((data) => {
          const htmlWithMergeTags = convertSunoPlaceholdersToMergeTags(
            data.html
          );
          onExportFromBuilder?.(htmlWithMergeTags);
        });
      }

      if (!isControlled) {
        setInternalTab(newValue);
      }

      onTabChange?.(newValue);
    },
    [
      currentTab,
      emailBuilderRef,
      onExportFromBuilder,
      isControlled,
      onTabChange,
      editorTabIsDisabled,
      confirmSwitchToEditor,
      dispatch,
      switchToEditor
    ]
  );

  // Reset state when showTabs changes to false
  useEffect(() => {
    if (!showTabs && !isControlled) {
      setInternalTab(EmailEditorTab.Editor);
    }
  }, [showTabs, isControlled]);

  const isEditorVisible = currentTab === EmailEditorTab.Editor;
  const isEmailBuilderVisible = currentTab === EmailEditorTab.EmailBuilder;

  // Defer mounting Unlayer until the builder tab is first opened (or a design is
  // loaded). Initializing while display:none breaks the custom Templates button.
  useEffect(() => {
    if (isEmailBuilderVisible || initialDesign) {
      setShouldMountBuilder(true);
    }
  }, [isEmailBuilderVisible, initialDesign]);

  const builderIsActive = isActive && isEmailBuilderVisible;

  const editorPane = fillContainer ? (
    <FillHeightEditorPane visible={isEditorVisible} onKeyDown={onKeyDown}>
      {editorContent}
    </FillHeightEditorPane>
  ) : (
    <Box sx={{ display: isEditorVisible ? "block" : "none" }}>
      {onKeyDown && isValidElement(editorContent)
        ? cloneElement(editorContent, { onKeyDown })
        : editorContent}
    </Box>
  );

  const builderPane =
    shouldMountBuilder &&
    (fillContainer ? (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: isEmailBuilderVisible ? "flex" : "none",
          flexDirection: "column"
        }}
      >
        <EmailBuilderComponent
          emailBuilderRef={emailBuilderRef}
          visible={isEmailBuilderVisible}
          initialMessage={initialMessage}
          initialDesign={initialDesign}
          setActiveDesign={setActiveDesign}
          setEditorTabIsDisabled={setEditorTabIsDisabled}
          onDesignChange={onDesignChange}
          templateTypes={templateTypes}
          isActive={builderIsActive}
          mergeTags={mergeTags}
          specialLinks={specialLinks}
          templateSource={templateSource}
          dialogZIndex={dialogZIndex}
          onTemplateInserted={onTemplateInserted}
          onEditorReady={onEditorReady}
          transformTemplateDesign={transformTemplateDesign}
          heightOffset={builderHeightOffset}
          fillContainer
        />
      </Box>
    ) : (
      <EmailBuilderComponent
        emailBuilderRef={emailBuilderRef}
        visible={isEmailBuilderVisible}
        initialMessage={initialMessage}
        initialDesign={initialDesign}
        setActiveDesign={setActiveDesign}
        setEditorTabIsDisabled={setEditorTabIsDisabled}
        onDesignChange={onDesignChange}
        templateTypes={templateTypes}
        isActive={builderIsActive}
        mergeTags={mergeTags}
        specialLinks={specialLinks}
        templateSource={templateSource}
        dialogZIndex={dialogZIndex}
        onTemplateInserted={onTemplateInserted}
        onEditorReady={onEditorReady}
        transformTemplateDesign={transformTemplateDesign}
        heightOffset={builderHeightOffset}
      />
    ));

  if (fillContainer) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {showTabs && (
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{ mb: 1, flexShrink: 0, ...tabsSx }}
          >
            <Tab
              label={tabLabels.editor}
              disabled={editorTabIsDisabled && !confirmSwitchToEditor}
            />
            <Tab label={tabLabels.emailBuilder} />
          </Tabs>
        )}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          {editorPane}
          {builderPane}
        </Box>
      </Box>
    );
  }

  return (
    <>
      {showTabs && (
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{ mb: 2, ...tabsSx }}
        >
          <Tab
            label={tabLabels.editor}
            disabled={editorTabIsDisabled && !confirmSwitchToEditor}
          />
          <Tab label={tabLabels.emailBuilder} />
        </Tabs>
      )}
      {contentWrapper ? (
        contentWrapper(
          <>
            {editorPane}
            {builderPane}
          </>
        )
      ) : (
        <>
          {editorPane}
          {builderPane}
        </>
      )}
    </>
  );
}

/**
 * Hook to manage email editor tabs state and export functionality
 */
export function useEmailEditorTabs() {
  const emailBuilderRef = useRef<UnlayerEditor | null>(null);

  const [activeTab, setActiveTab] = useState<EmailEditorTab>(
    EmailEditorTab.Editor
  );
  const [activeDesign, setActiveDesign] = useState<UnlayerDesign | null>(null);
  const [editorTabIsDisabled, setEditorTabIsDisabled] = useState(false);

  const isEmailBuilder = activeTab === EmailEditorTab.EmailBuilder;

  const exportHtml = useCallback((): Promise<{
    html: string;
    design: UnlayerDesign;
  }> => {
    if (!emailBuilderRef.current) {
      return Promise.reject(new Error("Email builder not initialized"));
    }
    return exportBuilderHtmlForStorage(emailBuilderRef.current);
  }, []);

  const loadDesign = useCallback((design: UnlayerDesign) => {
    setActiveTab(EmailEditorTab.EmailBuilder);
    setActiveDesign(design);
    setEditorTabIsDisabled(true);
  }, []);

  const reset = useCallback(() => {
    setActiveTab(EmailEditorTab.Editor);
  }, []);

  return {
    emailBuilderRef,
    activeTab,
    setActiveTab,
    isEmailBuilder,
    exportHtml,
    loadDesign,
    reset,
    activeDesign,
    setActiveDesign,
    editorTabIsDisabled,
    setEditorTabIsDisabled
  };
}
